import { useEffect, useRef, useState, MouseEvent as ReactMouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Share2,
  Facebook,
  Instagram,
  Mail,
  Link2,
  MessageCircle,
} from 'lucide-react';
import { useToast } from '../ui/UIContext';

interface ShareMenuProps {
  /** Canonical product URL — must be absolute (https://faticaftan.com/...). */
  url: string;
  /** Product title used as share message subject + Pinterest description. */
  title: string;
  /** Optional descriptor injected in email body + native share text. */
  description?: string;
  /** Optional absolute image URL used by Pinterest. Drops the Pin button when absent. */
  image?: string;
  /** `full` renders an outlined button with label (product detail surface).
   *  `icon` renders a circular icon-only button suitable for card overlays. */
  variant?: 'full' | 'icon';
  /** Optional extra class to position the trigger (e.g. absolute on a card). */
  className?: string;
}

/**
 * Product-level share menu. Used on the product detail page and as a hover
 * overlay on catalog cards. On devices that expose `navigator.share` we
 * surface the native OS share sheet; everything else gets a dropdown with
 * targeted platforms (WhatsApp, Facebook, Pinterest, Instagram-copy, email,
 * direct link copy).
 *
 * Instagram is intentionally a "copy link" action — Meta's app has no
 * URL-share intent on web, so the next best thing is to put the link on
 * the clipboard with a hint toast so the user can paste it in their story
 * or bio.
 */
export function ShareMenu({
  url,
  title,
  description,
  image,
  variant = 'full',
  className,
}: ShareMenuProps) {
  const { t } = useTranslation();
  const showToast = useToast();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  const hasNativeShare =
    typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  async function handleTrigger(e: ReactMouseEvent) {
    // Critical when the trigger sits inside an `<a>` (catalog card overlay) —
    // a bare click would otherwise navigate to the product detail page and
    // we'd never get the share intent.
    e.preventDefault();
    e.stopPropagation();
    if (hasNativeShare) {
      try {
        await navigator.share({ title, text: description, url });
        return;
      } catch (err) {
        // User cancellations are reported as AbortError — we silently swallow
        // those. Other errors fall through to the dropdown so the user can
        // still pick a target manually.
        if (err instanceof Error && err.name === 'AbortError') return;
      }
    }
    setOpen((v) => !v);
  }

  function stop(e: ReactMouseEvent) {
    e.stopPropagation();
  }

  async function copyToClipboard(): Promise<boolean> {
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }

  async function handleCopy(e: ReactMouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard();
    showToast(ok ? t('share.copiedToast') : t('share.copyFailed'), ok ? 'success' : 'error');
    if (ok) setOpen(false);
  }

  async function handleInstagram(e: ReactMouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    const ok = await copyToClipboard();
    showToast(ok ? t('share.instagramHint') : t('share.copyFailed'), ok ? 'info' : 'error');
    if (ok) setOpen(false);
  }

  const shareText = description ? `${title} — ${description}` : title;
  const waHref = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${url}`)}`;
  const fbHref = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
  const pinHref = image
    ? `https://www.pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&media=${encodeURIComponent(image)}&description=${encodeURIComponent(title)}`
    : null;
  const mailHref = `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(
    `${description ? description + '\n\n' : ''}${url}`,
  )}`;

  const triggerClass =
    variant === 'full'
      ? 'inline-flex items-center gap-2 px-4 py-2 border border-linen text-ink-soft hover:text-brass hover:border-brass transition-colors editorial-label'
      : 'inline-flex items-center justify-center w-9 h-9 rounded-full border bg-paper/80 backdrop-blur-sm text-ink hover:text-brass hover:border-brass border-linen transition-colors';

  return (
    <div ref={wrapRef} className={`relative inline-block ${className ?? ''}`}>
      <button
        type="button"
        onClick={handleTrigger}
        className={triggerClass}
        aria-label={t('share.button')}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Share2 className="w-4 h-4" aria-hidden="true" />
        {variant === 'full' && <span>{t('share.button')}</span>}
      </button>

      {open && (
        <div
          role="menu"
          onClick={stop}
          className="absolute end-0 mt-2 w-56 bg-paper border border-linen shadow-xl z-50 py-1"
        >
          <ShareItem
            href={waHref}
            label={t('share.targets.whatsapp')}
            icon={<MessageCircle className="w-4 h-4" />}
          />
          <ShareItem
            href={fbHref}
            label={t('share.targets.facebook')}
            icon={<Facebook className="w-4 h-4" />}
          />
          {pinHref && (
            <ShareItem
              href={pinHref}
              label={t('share.targets.pinterest')}
              icon={<PinterestIcon />}
            />
          )}
          <ShareItemButton
            onClick={handleInstagram}
            label={t('share.targets.instagram')}
            icon={<Instagram className="w-4 h-4" />}
          />
          <ShareItem
            href={mailHref}
            label={t('share.targets.email')}
            icon={<Mail className="w-4 h-4" />}
            external={false}
          />
          <ShareItemButton
            onClick={handleCopy}
            label={t('share.targets.copy')}
            icon={<Link2 className="w-4 h-4" />}
          />
        </div>
      )}
    </div>
  );
}

interface ShareItemProps {
  href: string;
  label: string;
  icon: React.ReactNode;
  external?: boolean;
}

function ShareItem({ href, label, icon, external = true }: ShareItemProps) {
  return (
    <a
      role="menuitem"
      href={href}
      target={external ? '_blank' : undefined}
      rel={external ? 'noopener noreferrer' : undefined}
      className="flex items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-linen/40 transition-colors"
    >
      <span className="text-brass" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </a>
  );
}

interface ShareItemButtonProps {
  onClick: (e: ReactMouseEvent) => void;
  label: string;
  icon: React.ReactNode;
}

function ShareItemButton({ onClick, label, icon }: ShareItemButtonProps) {
  return (
    <button
      role="menuitem"
      type="button"
      onClick={onClick}
      className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-ink hover:bg-linen/40 transition-colors text-start"
    >
      <span className="text-brass" aria-hidden="true">
        {icon}
      </span>
      <span>{label}</span>
    </button>
  );
}

/**
 * Pinterest brand mark — lucide-react ships no Pinterest icon so we inline
 * the rounded "P" glyph from the Pinterest brand kit (simplified path).
 */
function PinterestIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
    >
      <path d="M12 0C5.373 0 0 5.372 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.993 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.137.893 2.739a.36.36 0 0 1 .083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.631-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z" />
    </svg>
  );
}
